import { useCallback, useEffect, useMemo, useState } from 'react';
import { loadNikkeCatalog } from '../../nikke-catalog/api/nikkeCatalogApi.js';
import { siteImageUrl } from '../../../shared/domain/siteImages.js';
import {
  applyNikkeMasterIds,
  loadNikkeCatalogD1SyncStatus,
  loadNikkeMasterSyncStatus,
  previewNikkeMasterIds,
  saveNikkeCatalogDisplayDraft,
  syncNikkeCatalogToD1,
} from '../api/nikkeMasterSyncApi.js';
import {
  aliasStoragePolicy,
  buildNikkeDbSearchResults,
  buildNikkeDisplayDiff,
  buildNikkeDisplayDraft,
  conflictsForNikke,
  nikkeManagementAliases,
  summarizeExternalMasterPreview,
  validateNikkeAliasDraft,
} from '../domain/nikkeDbManagement.js';

const TABS = [
  { id: 'basic', label: '기본 정보' },
  { id: 'aliases', label: '별명' },
  { id: 'identifiers', label: '식별자' },
  { id: 'external', label: '외부 데이터' },
  { id: 'sync', label: '동기화 상태' },
  { id: 'new', label: '새 니케 추가' },
];

const FIELD_LABELS = {
  game_character_id: 'game_character_id',
  name_code: 'name_code',
  display_order: 'display_order',
  name: '공식명',
  rare: '등급',
  class: '클래스',
  corporation: '소속',
  element: '속성 코드',
  element_kr: '속성',
  element_icon_key: '속성 아이콘',
  element_image_url: '속성 이미지',
  burst: '버스트',
  burst_image_url: '버스트 이미지',
  weapon_type: '무기',
  attack_type: '공격 타입',
  costumes: '코스튬',
  is_visible: '노출',
};

function formatDateTime(value) {
  if (!value) return '없음';
  try {
    return new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return '확인 불가';
  }
}

function parseResourceIds(value) {
  const ids = [...new Set(String(value || '')
    .split(/[\s,]+/)
    .map((item) => Number(item))
    .filter((item) => Number.isInteger(item) && item >= 1 && item <= 9999))];
  if (!ids.length) throw new Error('니케 번호를 입력해주세요.');
  if (ids.length > 20) throw new Error('한 번에 최대 20개까지 입력할 수 있습니다.');
  return ids;
}

function ResultNames({ title, items = [] }) {
  if (!items.length) return null;
  return <div className="nikke-master-sync-result-names"><strong>{title}</strong><p>{items.map((item) => `${item.name || '이름 확인 불가'} (#${item.resourceId})`).join(', ')}</p></div>;
}

function InfoRow({ label, value }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value || '없음'}</dd>
    </div>
  );
}

function DiffRows({ items = [] }) {
  if (!items.length) return <p className="nikke-db-management-muted">변경 예정인 필드가 없습니다.</p>;
  return (
    <div className="nikke-db-management-diff-list">
      {items.map((item) => (
        <div key={item.key}>
          <strong>{item.label}</strong>
          <span>{item.before || '없음'}</span>
          <b aria-hidden="true">→</b>
          <span>{item.after || '없음'}</span>
        </div>
      ))}
    </div>
  );
}

export function NikkeMasterSyncBoard({ mobile = false }) {
  const [status, setStatus] = useState(null);
  const [d1Status, setD1Status] = useState(null);
  const [catalog, setCatalog] = useState({ items: [], lookup: null, source: '', warnings: [] });
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [activeTab, setActiveTab] = useState('basic');
  const [draft, setDraft] = useState(null);
  const [aliasInput, setAliasInput] = useState('');
  const [resourceInput, setResourceInput] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [preview, setPreview] = useState(null);
  const [externalPreview, setExternalPreview] = useState(null);
  const [working, setWorking] = useState('');
  const [notice, setNotice] = useState({ type: '', message: '' });

  const refresh = useCallback(async ({ quiet = false, forceCatalog = false } = {}) => {
    if (!quiet) setWorking('refresh');
    const [statusResult, d1Result, catalogResult] = await Promise.allSettled([
      loadNikkeMasterSyncStatus(),
      loadNikkeCatalogD1SyncStatus(),
      loadNikkeCatalog({ includeInactive: true, forceRefresh: forceCatalog }),
    ]);

    if (statusResult.status === 'fulfilled') setStatus(statusResult.value);
    if (d1Result.status === 'fulfilled') setD1Status(d1Result.value);
    if (catalogResult.status === 'fulfilled') {
      setCatalog({
        items: catalogResult.value.rows || catalogResult.value.lookup?.items || [],
        lookup: catalogResult.value.lookup,
        source: catalogResult.value.source || '',
        warnings: catalogResult.value.warnings || [],
      });
    }

    const firstError = [statusResult, d1Result, catalogResult].find((result) => result.status === 'rejected');
    if (firstError && !quiet) {
      setNotice({ type: 'error', message: firstError.reason?.message || '니케 DB 관리 정보를 불러오지 못했습니다.' });
    }
    if (!firstError && !quiet) setNotice({ type: '', message: '' });
    if (!quiet) setWorking('');
  }, []);

  useEffect(() => { refresh({ forceCatalog: true }); }, [refresh]);

  const searchResults = useMemo(
    () => buildNikkeDbSearchResults({ items: catalog.items, lookup: catalog.lookup }, query),
    [catalog.items, catalog.lookup, query],
  );

  useEffect(() => {
    if (selectedId && catalog.items.some((item) => item.nikkeId === selectedId)) return;
    setSelectedId(searchResults[0]?.nikkeId || catalog.items[0]?.nikkeId || '');
  }, [catalog.items, searchResults, selectedId]);

  const selected = useMemo(
    () => catalog.items.find((item) => item.nikkeId === selectedId) || null,
    [catalog.items, selectedId],
  );

  const selectedSignature = selected
    ? [
      selected.nikkeId,
      selected.name,
      selected.imageUrl,
      selected.element,
      selected.burst,
      selected.active,
      nikkeManagementAliases(selected).join('|'),
    ].join('::')
    : '';

  useEffect(() => {
    if (!selected) {
      setDraft(null);
      return;
    }
    setDraft(buildNikkeDisplayDraft(selected));
    setAliasInput('');
    setExternalPreview(null);
  }, [selected, selectedSignature]);

  const aliasValidation = useMemo(() => validateNikkeAliasDraft({
    catalog: catalog.lookup,
    targetNikkeId: selected?.nikkeId,
    aliases: draft?.aliases || [],
  }), [catalog.lookup, draft?.aliases, selected?.nikkeId]);

  const identifierConflicts = useMemo(
    () => conflictsForNikke(catalog.items, selected?.nikkeId || ''),
    [catalog.items, selected?.nikkeId],
  );

  const diff = useMemo(
    () => selected && draft ? buildNikkeDisplayDiff(selected, draft) : [],
    [selected, draft],
  );

  // 이미지 URL 칸은 DB 원본이고, 사이트는 siteImages 가 바꿔치기한 doroparty-assets 사본을 그린다.
  // 사본이 없는 니케(마지막 동기화 이후 추가된 것 등)는 원본 주소가 그대로 나가므로 그것도 알린다.
  const servedImageNote = useMemo(() => {
    const original = String(draft?.imageUrl || '').trim();
    if (!original) return '이미지 주소가 비어 있습니다.';
    const served = siteImageUrl(original);
    if (served === original) return '사본이 없어 위 원본 주소를 그대로 불러옵니다. site-images 동기화가 필요합니다.';
    return `사이트는 doroparty-assets 사본을 불러옵니다 · ${served.split('/').pop()}`;
  }, [draft?.imageUrl]);

  const latestBatch = status?.latestBatch || null;
  const policy = aliasStoragePolicy();

  const previewIds = async () => {
    if (working) return;
    let ids;
    try {
      ids = parseResourceIds(resourceInput);
    } catch (error) {
      setNotice({ type: 'error', message: error?.message || '니케 번호를 확인해주세요.' });
      return;
    }
    setWorking('preview');
    setNotice({ type: '', message: '' });
    setPreview(null);
    try {
      const result = await previewNikkeMasterIds(ids);
      setSelectedIds(ids);
      setPreview(result);
      setNotice({
        type: 'success',
        message: `조회 완료 · 신규 ${result.insertedCount}명 · 변경 ${result.updatedCount}명 · 유지 ${result.unchangedCount}명`,
      });
    } catch (error) {
      setSelectedIds([]);
      setNotice({ type: 'error', message: error?.message || '입력한 니케 정보를 조회하지 못했습니다.' });
    } finally {
      setWorking('');
    }
  };

  const apply = async () => {
    if (!preview?.complete || !selectedIds.length || working) return;
    if (!window.confirm(`${selectedIds.join(', ')}번 니케 정보를 공용 DB에 반영할까요?\nSupabase 반영 후 Cloudflare D1까지 동기화합니다.\n기존 캐릭터는 삭제하지 않습니다.`)) return;
    setWorking('apply');
    setNotice({ type: '', message: '' });
    try {
      const result = await applyNikkeMasterIds(selectedIds);
      setPreview(result);
      setNotice({
        type: 'success',
        message: `공용 니케 DB + D1 반영 완료 · 신규 ${result.insertedCount}명 · 변경 ${result.updatedCount}명 · 유지 ${result.unchangedCount}명 · D1 확인 ${result.d1Sync?.verifiedCount ?? selectedIds.length}명`,
      });
      await refresh({ quiet: true, forceCatalog: true });
      window.dispatchEvent(new CustomEvent('dororaid:nikke-catalog-changed'));
    } catch (error) {
      setNotice({ type: 'error', message: error?.message || '공용 니케 DB 반영에 실패했습니다.' });
    } finally {
      setWorking('');
    }
  };

  const previewSelectedExternal = async () => {
    if (!selected?.resourceId || working) return;
    setWorking('external-preview');
    setExternalPreview(null);
    setNotice({ type: '', message: '' });
    try {
      setExternalPreview(await previewNikkeMasterIds([selected.resourceId]));
    } catch (error) {
      setNotice({ type: 'error', message: error?.message || '외부 데이터를 다시 조회하지 못했습니다.' });
    } finally {
      setWorking('');
    }
  };

  const applySelectedExternal = async () => {
    if (!selected?.resourceId || !externalPreview?.complete || working) return;
    if (!window.confirm(`${selected.name} 외부 마스터 정보를 공용 DB에 반영할까요?\n표시명과 별명은 nikke_db 보정값으로 유지됩니다.`)) return;
    setWorking('external-apply');
    setNotice({ type: '', message: '' });
    try {
      const result = await applyNikkeMasterIds([selected.resourceId]);
      setExternalPreview(result);
      setNotice({
        type: 'success',
        message: `외부 마스터 반영 완료 · D1 확인 ${result.d1Sync?.verifiedCount ?? 1}명`,
      });
      await refresh({ quiet: true, forceCatalog: true });
      window.dispatchEvent(new CustomEvent('dororaid:nikke-catalog-changed'));
    } catch (error) {
      setNotice({ type: 'error', message: error?.message || '외부 마스터 반영에 실패했습니다.' });
    } finally {
      setWorking('');
    }
  };

  const saveDisplayDraft = async () => {
    if (!selected || !draft || working) return;
    if (!aliasValidation.ok) {
      setNotice({ type: 'error', message: aliasValidation.errors[0] || '별명 충돌을 먼저 해결해주세요.' });
      return;
    }
    if (identifierConflicts.length) {
      setNotice({ type: 'error', message: `${identifierConflicts[0].type} ${identifierConflicts[0].value} 식별자가 다른 NIKKE와 충돌합니다.` });
      return;
    }
    if (!diff.length) {
      setNotice({ type: 'success', message: '변경할 표시 정보가 없습니다.' });
      return;
    }
    if (!window.confirm(`${selected.name} 표시 정보를 저장할까요?\n저장 후 Cloudflare D1 카탈로그를 같은 니케만 다시 동기화합니다.`)) return;

    setWorking('save-display');
    setNotice({ type: '', message: '' });
    try {
      const result = await saveNikkeCatalogDisplayDraft(selected, {
        ...draft,
        aliases: aliasValidation.aliases,
      });
      setNotice({
        type: 'success',
        message: `니케 DB 관리 정보 저장 완료 · D1 확인 ${result.d1Sync?.verifiedCount ?? 1}명`,
      });
      await refresh({ quiet: true, forceCatalog: true });
      window.dispatchEvent(new CustomEvent('dororaid:nikke-catalog-changed'));
    } catch (error) {
      setNotice({ type: 'error', message: error?.message || '니케 표시 정보를 저장하지 못했습니다.' });
    } finally {
      setWorking('');
    }
  };

  const addAlias = () => {
    if (!draft || !aliasInput.trim()) return;
    const next = [...(draft.aliases || []), aliasInput];
    const validation = validateNikkeAliasDraft({ catalog: catalog.lookup, targetNikkeId: selected?.nikkeId, aliases: next });
    if (!validation.ok) {
      setNotice({ type: 'error', message: validation.errors[0] || '별명 충돌을 먼저 해결해주세요.' });
      return;
    }
    setDraft((current) => ({ ...current, aliases: validation.aliases }));
    setAliasInput('');
    setNotice({ type: '', message: '' });
  };

  const removeAlias = (alias) => {
    setDraft((current) => ({
      ...current,
      aliases: (current?.aliases || []).filter((item) => item !== alias),
    }));
  };

  const syncD1Full = async () => {
    if (working) return;
    if (!window.confirm('Supabase 공용 니케 DB 전체를 Cloudflare D1에 다시 동기화할까요?\n기존 캐릭터를 삭제하지 않고 최신 행을 upsert합니다.')) return;
    setWorking('d1-full');
    setNotice({ type: '', message: '' });
    try {
      const result = await syncNikkeCatalogToD1([], { full: true });
      setNotice({
        type: 'success',
        message: `D1 전체 동기화 완료 · 마스터 ${result.masterCount}명 · 표시 DB ${result.displayCount}명 · 검증 ${result.verifiedCount}명`,
      });
      await refresh({ quiet: true, forceCatalog: true });
      window.dispatchEvent(new CustomEvent('dororaid:nikke-catalog-changed'));
    } catch (error) {
      setNotice({ type: 'error', message: error?.message || 'Cloudflare D1 전체 동기화에 실패했습니다.' });
    } finally {
      setWorking('');
    }
  };

  const clear = () => {
    setResourceInput('');
    setSelectedIds([]);
    setPreview(null);
    setNotice({ type: '', message: '' });
  };

  const externalSummary = summarizeExternalMasterPreview(externalPreview);

  return (
    <section className={`nikke-master-sync-board nikke-db-management ${mobile ? 'mobile' : 'desktop'}`} aria-label="니케 DB 관리">
      <header>
        <div>
          <p className="section-eyebrow">플랫폼 관리</p>
          <h2>니케 DB 관리</h2>
          <p>공용 카탈로그의 검색, 별명, 식별자, 외부 데이터 재조회, Cloudflare D1 동기화를 한 곳에서 확인합니다.</p>
        </div>
        <button type="button" className="secondary" disabled={Boolean(working)} onClick={() => refresh({ forceCatalog: true })}>{working === 'refresh' ? '새로고침 중…' : '새로고침'}</button>
      </header>

      <div className="nikke-master-sync-kpis">
        <article><span>현재 공용 DB</span><strong>{Number(status?.visibleCount || catalog.items.length || 0).toLocaleString('ko-KR')}명</strong><small>활성 니케 기준</small></article>
        <article><span>신규 수집</span><strong>서버 직접 조회</strong><small>Tampermonkey 불필요</small></article>
        <article><span>최근 반영</span><strong>{latestBatch?.applied ? `${latestBatch.inserted_count || 0}+${latestBatch.updated_count || 0}` : '없음'}</strong><small>{formatDateTime(latestBatch?.created_at)}</small></article>
      </div>

      <div className="nikke-db-management-read-path">
        <strong>읽기 경로</strong>
        <span>{catalog.source === 'cloudflare-d1' ? 'D1 우선' : catalog.source || '확인 중'}</span>
        <small>{catalog.warnings[0] || 'Supabase는 실패 fallback'}</small>
      </div>

      <div className="nikke-db-management-layout">
        <aside className="nikke-db-management-search">
          <label>
            <span>검색</span>
            <input
              type="search"
              value={query}
              placeholder="이름 / 별명 / ID / 코드"
              disabled={Boolean(working)}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <div className="nikke-db-management-result-count">
            <strong>{searchResults.length.toLocaleString('ko-KR')}</strong>
            <span>{query.trim() ? '검색 결과' : '등록된 니케'}</span>
          </div>
          <div className="nikke-db-management-result-list">
            {searchResults.map((item) => (
              <button
                type="button"
                key={item.nikkeId}
                className={item.nikkeId === selectedId ? 'active' : ''}
                onClick={() => setSelectedId(item.nikkeId)}
              >
                <strong>{item.name}</strong>
                <small>{item.nikkeId} · #{item.resourceId || '없음'} · name_code {item.nameCode || '없음'}</small>
                {nikkeManagementAliases(item).length ? <span>{nikkeManagementAliases(item).join(' · ')}</span> : null}
              </button>
            ))}
            {!searchResults.length ? <p>검색 결과가 없습니다.</p> : null}
          </div>
        </aside>

        <div className="nikke-db-management-detail">
          <nav className="nikke-db-management-tabs" aria-label="니케 DB 관리 탭">
            {TABS.map((tab) => (
              <button
                type="button"
                key={tab.id}
                className={activeTab === tab.id ? 'active' : ''}
                aria-pressed={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          {activeTab !== 'new' && activeTab !== 'sync' && selected && draft ? (
            <article className="nikke-db-management-card">
              <header className="nikke-db-management-selected">
                <div>
                  <p className="section-eyebrow">니케 상세</p>
                  <h3>{selected.name}</h3>
                  <p>{selected.officialName || '외부 공식명 없음'} · {selected.nikkeId}</p>
                </div>
                {/* 여기만 원본 URL을 그대로 그린다(test-site-images.mjs 가 예외로 적어 둔 곳).
                    원본이 죽었는지 확인하는 화면이라 사본으로 바꾸면 깨진 주소가 안 보인다. */}
                <img src={selected.imageUrl} alt="" loading="lazy" />
              </header>

              {activeTab === 'basic' ? (
                <div className="nikke-db-management-basic">
                  <label>
                    <span>표시명</span>
                    <input value={draft.name} maxLength={80} onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))} />
                  </label>
                  <label>
                    <span>이미지 URL</span>
                    <input value={draft.imageUrl} onChange={(event) => setDraft((current) => ({ ...current, imageUrl: event.target.value }))} />
                  </label>
                  {/* 이 칸은 DB 원본 값이고 사이트가 실제로 받아오는 주소가 아니다. 둘이 같아 보이면
                      사이트가 외부 레포에 물려 있는 것처럼 읽히므로 어느 쪽을 그리는지 적어 둔다. */}
                  <small className="nikke-db-management-muted">{servedImageNote}</small>
                  <label className="nikke-db-management-check">
                    <input type="checkbox" checked={draft.active !== false} onChange={(event) => setDraft((current) => ({ ...current, active: event.target.checked }))} />
                    <span>활성 카탈로그에 표시</span>
                  </label>
                  <dl className="nikke-db-management-info-grid">
                    <InfoRow label="공식명" value={selected.officialName} />
                    <InfoRow label="속성" value={selected.element} />
                    <InfoRow label="버스트" value={selected.burst} />
                    <InfoRow label="무기" value={selected.weaponType} />
                    <InfoRow label="클래스" value={selected.className} />
                    <InfoRow label="소속" value={selected.corporation} />
                  </dl>
                  <div className="nikke-db-management-preview">
                    <strong>변경 미리보기</strong>
                    <DiffRows items={diff} />
                  </div>
                  <button type="button" className="apply" disabled={Boolean(working) || !diff.length} onClick={saveDisplayDraft}>
                    {working === 'save-display' ? '저장 중…' : '표시 정보 저장'}
                  </button>
                </div>
              ) : null}

              {activeTab === 'aliases' ? (
                <div className="nikke-db-management-aliases">
                  <div className="nikke-db-management-policy">
                    <strong>별명 저장 방식</strong>
                    <p>현재 production schema는 <code>nikke_db.alias1</code> / <code>alias2</code> 두 칸입니다. 별도 <code>nikke_aliases</code> 테이블은 후속 migration 대상입니다.</p>
                  </div>
                  <div className="nikke-db-management-alias-list">
                    {draft.aliases.length ? draft.aliases.map((alias) => (
                      <span key={alias}>
                        {alias}
                        <button type="button" aria-label={`${alias} 별명 삭제`} onClick={() => removeAlias(alias)}>삭제</button>
                      </span>
                    )) : <p>등록된 별명이 없습니다.</p>}
                  </div>
                  <div className="nikke-db-management-alias-add">
                    <input
                      value={aliasInput}
                      maxLength={40}
                      placeholder={policy.maxAliases > draft.aliases.length ? '별명 추가' : '현재 스키마 한도 도달'}
                      disabled={policy.maxAliases <= draft.aliases.length}
                      onChange={(event) => setAliasInput(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') addAlias();
                      }}
                    />
                    <button type="button" className="secondary" disabled={!aliasInput.trim() || policy.maxAliases <= draft.aliases.length} onClick={addAlias}>추가</button>
                  </div>
                  {!aliasValidation.ok ? <p className="nikke-master-sync-notice error">{aliasValidation.errors[0]}</p> : null}
                  <div className="nikke-db-management-preview">
                    <strong>변경 미리보기</strong>
                    <DiffRows items={diff.filter((item) => item.key.startsWith('alias'))} />
                  </div>
                  <button type="button" className="apply" disabled={Boolean(working) || !diff.length || !aliasValidation.ok} onClick={saveDisplayDraft}>
                    {working === 'save-display' ? '저장 중…' : '별명 저장'}
                  </button>
                </div>
              ) : null}

              {activeTab === 'identifiers' ? (
                <div className="nikke-db-management-identifiers">
                  <dl className="nikke-db-management-info-grid">
                    <InfoRow label="canonical nikke_id" value={selected.nikkeId} />
                    <InfoRow label="resource_id" value={selected.resourceId} />
                    <InfoRow label="game_character_id" value={selected.gameCharacterId} />
                    <InfoRow label="name_code" value={selected.nameCode} />
                  </dl>
                  <div className="nikke-db-management-source-ids">
                    <strong>source identifiers</strong>
                    {selected.sourceIdentifiers?.length ? (
                      <p>{selected.sourceIdentifiers.join(' · ')}</p>
                    ) : (
                      <p>추가 source identifier 없음</p>
                    )}
                  </div>
                  {identifierConflicts.length ? (
                    <div className="nikke-master-sync-notice error">
                      {identifierConflicts.map((conflict) => (
                        <p key={`${conflict.type}:${conflict.value}`}>{conflict.type} {conflict.value} 충돌 · {conflict.items.map((item) => item.name).join(', ')}</p>
                      ))}
                    </div>
                  ) : (
                    <p className="nikke-master-sync-notice success">현재 catalog 기준 강한 식별자 충돌이 없습니다.</p>
                  )}
                </div>
              ) : null}

              {activeTab === 'external' ? (
                <div className="nikke-db-management-external">
                  <div className="nikke-db-management-policy">
                    <strong>외부 데이터 다시 조회</strong>
                    <p>조회는 변경 미리보기만 만들며, 적용 버튼을 누르기 전에는 DB를 덮어쓰지 않습니다.</p>
                  </div>
                  <div className="nikke-master-sync-actions">
                    <button type="button" disabled={Boolean(working) || !selected.resourceId} onClick={previewSelectedExternal}>
                      {working === 'external-preview' ? '조회 중…' : '외부 데이터 다시 조회'}
                    </button>
                    <button type="button" className="apply" disabled={Boolean(working) || !externalPreview?.complete} onClick={applySelectedExternal}>
                      {working === 'external-apply' ? '적용 중…' : '외부 마스터 적용'}
                    </button>
                  </div>
                  {externalPreview ? (
                    <div className={`nikke-master-sync-validation ${externalPreview.complete ? 'complete' : 'partial'}`}>
                      <div>
                        <span>조회 번호</span>
                        <strong>{selected.resourceId}</strong>
                        <small>{externalPreview.capturedCount} / {externalPreview.expectedCount}명 확인</small>
                      </div>
                      <div className="nikke-master-sync-validation-state">
                        <strong>{externalSummary.hasChanges ? '변경 있음' : '변경 없음'}</strong>
                        {externalSummary.warnings[0] ? <small>{externalSummary.warnings[0]}</small> : null}
                      </div>
                    </div>
                  ) : null}
                  {externalSummary.changed.length ? (
                    <div className="nikke-db-management-external-diff">
                      {externalSummary.changed.map((change) => (
                        <section key={`${change.changeType}:${change.resourceId}`}>
                          <strong>{change.name} #{change.resourceId}</strong>
                          {change.fields.map((field) => (
                            <div key={field.key}>
                              <span>{FIELD_LABELS[field.key] || field.key}</span>
                              <code>{String(field.before ?? '없음')}</code>
                              <b aria-hidden="true">→</b>
                              <code>{String(field.after ?? '없음')}</code>
                            </div>
                          ))}
                        </section>
                      ))}
                    </div>
                  ) : null}
                  <ResultNames title="신규 후보" items={externalSummary.inserted} />
                  <ResultNames title="변경 후보" items={externalSummary.updated} />
                </div>
              ) : null}

            </article>
          ) : null}

          {/* 동기화 상태는 선택한 니케와 무관한 전체 수치인데 캐릭터 상세 카드 안에 들어 있어서
              엠마의 초상화 밑에 D1 전체 개수가 붙어 나왔다. 별도 카드로 분리한다. */}
          {activeTab === 'sync' ? (
            <article className="nikke-db-management-card">
              <header className="nikke-db-management-selected">
                <div>
                  <p className="section-eyebrow">공용 DB 동기화</p>
                  <h3>Supabase · Cloudflare D1</h3>
                  <p>선택한 니케와 관계없는 공용 DB 전체 수치입니다.</p>
                </div>
              </header>
              <div className="nikke-db-management-sync">
                <dl className="nikke-db-management-info-grid">
                  <InfoRow label="Supabase 전체" value={`${Number(status?.currentCount || 0).toLocaleString('ko-KR')}명`} />
                  <InfoRow label="Supabase 활성" value={`${Number(status?.visibleCount || 0).toLocaleString('ko-KR')}명`} />
                  <InfoRow label="D1 전체" value={`${Number(d1Status?.masterCount || 0).toLocaleString('ko-KR')}명`} />
                  <InfoRow label="D1 활성" value={`${Number(d1Status?.activeCount || 0).toLocaleString('ko-KR')}명`} />
                  <InfoRow label="D1 최근 동기화" value={formatDateTime(d1Status?.metadata?.nikke_catalog_sync_completed_at?.value)} />
                  <InfoRow label="원본 갱신" value={formatDateTime(d1Status?.metadata?.nikke_catalog_sync_source_updated_at?.value)} />
                </dl>
                <button type="button" className="secondary" disabled={Boolean(working)} onClick={syncD1Full}>
                  {working === 'd1-full' ? 'D1 동기화 중…' : 'D1 전체 동기화'}
                </button>
              </div>
            </article>
          ) : null}

          {activeTab === 'new' ? (
            <article className="nikke-db-management-card">
              <div className="nikke-master-sync-source">
                <div>
                  <strong>새 니케 추가</strong>
                  <p>BlaBlaLink 니케 번호를 쉼표 또는 띄어쓰기로 최대 20개까지 입력합니다.</p>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={resourceInput}
                    placeholder="니케 번호 입력"
                    aria-label="BlaBlaLink 니케 번호"
                    disabled={Boolean(working)}
                    onChange={(event) => {
                      setResourceInput(event.target.value);
                      setPreview(null);
                      setSelectedIds([]);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') previewIds();
                    }}
                  />
                </div>
                <div className="nikke-master-sync-actions">
                  <button type="button" disabled={Boolean(working) || !resourceInput.trim()} onClick={previewIds}>{working === 'preview' ? '조회 중…' : '조회'}</button>
                  <button type="button" className="apply" disabled={!preview?.complete || Boolean(working)} onClick={apply}>{working === 'apply' ? '적용 중…' : 'DB 적용'}</button>
                  <button type="button" className="secondary" disabled={Boolean(working) || (!resourceInput && !preview)} onClick={clear}>초기화</button>
                </div>
              </div>

              <div className={`nikke-master-sync-validation ${preview ? preview.complete ? 'complete' : 'partial' : 'empty'}`}>
                <div>
                  <span>조회 번호</span>
                  <strong>{selectedIds.length ? selectedIds.join(', ') : '없음'}</strong>
                  <small>{preview ? `${preview.capturedCount} / ${preview.expectedCount}명 확인` : '니케 번호를 입력한 뒤 조회를 누르세요.'}</small>
                </div>
                <div className="nikke-master-sync-validation-state">
                  <strong>{preview ? preview.complete ? '검증 완료' : '조회 불완전' : '대기'}</strong>
                  {preview?.warnings?.length ? <small>{preview.warnings[0]}</small> : null}
                </div>
              </div>

              {preview ? (
                <div className="nikke-master-sync-result">
                  <div><span>신규</span><strong>{preview.insertedCount}</strong></div>
                  <div><span>변경</span><strong>{preview.updatedCount}</strong></div>
                  <div><span>유지</span><strong>{preview.unchangedCount}</strong></div>
                  <ResultNames title="신규 후보" items={preview.insertedCharacters} />
                  <ResultNames title="변경 후보" items={preview.updatedCharacters} />
                </div>
              ) : null}
            </article>
          ) : null}
        </div>
      </div>

      {notice.message ? <p className={`nikke-master-sync-notice ${notice.type}`} role="status">{notice.message}</p> : null}
    </section>
  );
}
